import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  GraduationCap, BookOpen, Award, Plus, TrendingUp, 
  Calendar, Clock, CheckCircle, AlertTriangle, Star,
  Trophy, Target
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const gradePoints = {
  'A+': 4.0, 'A': 4.0, 'A-': 3.7,
  'B+': 3.3, 'B': 3.0, 'B-': 2.7,
  'C+': 2.3, 'C': 2.0, 'C-': 1.7,
  'D+': 1.3, 'D': 1.0, 'D-': 0.7,
  'F': 0.0
};

const gradeOptions = Object.keys(gradePoints);

const AcademicsPage = () => {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [showAddAchievement, setShowAddAchievement] = useState(false);
  
  const [newCourse, setNewCourse] = useState({
    name: '',
    grade: 'A',
    credits: 1,
    teacher: '',
    notes: ''
  });

  const [newAchievement, setNewAchievement] = useState({
    title: '',
    type: 'honor_roll',
    date: new Date().toISOString().split('T')[0],
    description: ''
  });

  useEffect(() => {
    fetchAcademicData();
  }, []);

  const fetchAcademicData = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/academics`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setCourses(response.data.courses || []);
      setAchievements(response.data.achievements || []);
    } catch (error) {
      // If no data yet, use empty arrays
      setCourses([]);
      setAchievements([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateGPA = () => {
    if (courses.length === 0) return 0;
    
    let totalPoints = 0;
    let totalCredits = 0;
    
    courses.forEach(course => {
      const points = gradePoints[course.grade] || 0;
      const credits = course.credits || 1;
      totalPoints += points * credits;
      totalCredits += credits;
    });
    
    return totalCredits > 0 ? (totalPoints / totalCredits).toFixed(2) : 0;
  };

  const addCourse = async () => {
    if (!newCourse.name) {
      toast.error('Course name is required');
      return;
    }

    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/academics/course`, newCourse, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Course added!');
      setShowAddCourse(false);
      setNewCourse({ name: '', grade: 'A', credits: 1, teacher: '', notes: '' });
      fetchAcademicData();
    } catch (error) {
      toast.error('Failed to add course');
    }
  };

  const addAchievement = async () => {
    if (!newAchievement.title) {
      toast.error('Achievement title is required');
      return;
    }

    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/academics/achievement`, newAchievement, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Achievement added!');
      setShowAddAchievement(false);
      setNewAchievement({ title: '', type: 'honor_roll', date: new Date().toISOString().split('T')[0], description: '' });
      fetchAcademicData();
    } catch (error) {
      toast.error('Failed to add achievement');
    }
  };

  const deleteCourse = async (courseId) => {
    if (!confirm('Remove this course?')) return;
    
    try {
      const token = localStorage.getItem('aura_token');
      await axios.delete(`${API}/academics/course/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Course removed');
      fetchAcademicData();
    } catch (error) {
      toast.error('Failed to remove course');
    }
  };

  const gpa = calculateGPA();
  const isEligible = gpa >= 2.0;
  const isHonorRoll = gpa >= 3.5;

  const getGPAColor = () => {
    if (gpa >= 3.5) return 'text-aura-success';
    if (gpa >= 3.0) return 'text-aura-primary';
    if (gpa >= 2.0) return 'text-aura-warning';
    return 'text-aura-destructive';
  };

  const achievementTypes = [
    { id: 'honor_roll', name: 'Honor Roll', icon: Star },
    { id: 'principal_list', name: "Principal's List", icon: Award },
    { id: 'perfect_attendance', name: 'Perfect Attendance', icon: Calendar },
    { id: 'academic_award', name: 'Academic Award', icon: Trophy },
    { id: 'citizenship', name: 'Citizenship Award', icon: CheckCircle },
    { id: 'other', name: 'Other Achievement', icon: Target },
  ];

  return (
    <DashboardLayout title="Academics">
      <div className="px-4 py-6 max-w-7xl mx-auto space-y-6">
        {/* Eligibility Banner */}
        <Card className={`border-white/10 ${isEligible ? 'bg-aura-success/10' : 'bg-aura-destructive/10'}`}>
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isEligible ? (
                <CheckCircle className="w-6 h-6 text-aura-success" />
              ) : (
                <AlertTriangle className="w-6 h-6 text-aura-destructive" />
              )}
              <div>
                <p className={`font-heading font-bold uppercase tracking-tight ${isEligible ? 'text-aura-success' : 'text-aura-destructive'}`}>
                  {isEligible ? 'Eligible to Play' : 'Academic Warning'}
                </p>
                <p className="text-xs text-aura-text-muted">
                  {isEligible 
                    ? 'Your GPA meets athletic eligibility requirements' 
                    : 'GPA must be 2.0+ to maintain athletic eligibility'}
                </p>
              </div>
            </div>
            {isHonorRoll && (
              <Badge className="bg-yellow-500/20 text-yellow-400 border-0">
                <Star className="w-3 h-3 mr-1" />
                Honor Roll
              </Badge>
            )}
          </CardContent>
        </Card>

        {/* GPA Card */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-aura-surface border-white/10 md:col-span-1">
            <CardContent className="p-6 text-center">
              <GraduationCap className="w-12 h-12 mx-auto text-aura-secondary mb-2" />
              <p className="text-xs text-aura-text-muted uppercase tracking-wider mb-1">Current GPA</p>
              <p className={`font-heading font-bold text-5xl ${getGPAColor()}`}>{gpa}</p>
              <p className="text-sm text-aura-text-muted mt-2">
                {courses.length} course{courses.length !== 1 ? 's' : ''} tracked
              </p>
              <div className="mt-4">
                <Progress 
                  value={(gpa / 4.0) * 100} 
                  className="h-2 bg-white/10"
                />
                <div className="flex justify-between text-xs text-aura-text-subtle mt-1">
                  <span>0.0</span>
                  <span>4.0</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-aura-surface border-white/10 md:col-span-2">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading font-bold text-sm text-aura-text-muted uppercase tracking-wider">
                  Why Academics Matter
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                <Target className="w-5 h-5 text-aura-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-white">Eligibility</p>
                  <p className="text-xs text-aura-text-muted">Most schools require 2.0+ GPA to participate in sports</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                <Trophy className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-white">College Recruiting</p>
                  <p className="text-xs text-aura-text-muted">NCAA requires minimum GPA for athletic scholarships</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                <TrendingUp className="w-5 h-5 text-aura-success flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-white">Character</p>
                  <p className="text-xs text-aura-text-muted">Academic discipline translates to athletic discipline</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Courses Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-lg text-white uppercase tracking-wider">
              Current Courses
            </h2>
            <Dialog open={showAddCourse} onOpenChange={setShowAddCourse}>
              <DialogTrigger asChild>
                <Button className="bg-aura-secondary hover:bg-aura-secondary/90 text-white" data-testid="add-course-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Course
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-aura-surface border-white/10 text-white">
                <DialogHeader>
                  <DialogTitle className="font-heading uppercase tracking-tight">Add Course</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Course Name *</Label>
                    <Input
                      value={newCourse.name}
                      onChange={(e) => setNewCourse({ ...newCourse, name: e.target.value })}
                      placeholder="e.g., English 8, Algebra 1"
                      className="bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Current Grade</Label>
                      <Select value={newCourse.grade} onValueChange={(v) => setNewCourse({ ...newCourse, grade: v })}>
                        <SelectTrigger className="bg-aura-bg border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-aura-surface border-white/10">
                          {gradeOptions.map(g => (
                            <SelectItem key={g} value={g} className="text-white">{g}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Credits</Label>
                      <Select value={newCourse.credits.toString()} onValueChange={(v) => setNewCourse({ ...newCourse, credits: parseInt(v) })}>
                        <SelectTrigger className="bg-aura-bg border-white/10 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-aura-surface border-white/10">
                          {[0.5, 1, 1.5, 2].map(c => (
                            <SelectItem key={c} value={c.toString()} className="text-white">{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Teacher (Optional)</Label>
                    <Input
                      value={newCourse.teacher}
                      onChange={(e) => setNewCourse({ ...newCourse, teacher: e.target.value })}
                      placeholder="e.g., Mrs. Johnson"
                      className="bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                  <Button onClick={addCourse} className="w-full bg-aura-secondary hover:bg-aura-secondary/90 text-white">
                    Add Course
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {courses.length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {courses.map((course) => (
                <Card key={course.course_id} className="bg-aura-surface border-white/10">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-heading font-bold text-white uppercase tracking-tight">
                          {course.name}
                        </p>
                        {course.teacher && (
                          <p className="text-xs text-aura-text-muted mt-1">{course.teacher}</p>
                        )}
                      </div>
                      <div className={`text-2xl font-heading font-bold ${
                        gradePoints[course.grade] >= 3.0 ? 'text-aura-success' :
                        gradePoints[course.grade] >= 2.0 ? 'text-aura-warning' :
                        'text-aura-destructive'
                      }`}>
                        {course.grade}
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
                      <span className="text-xs text-aura-text-muted">{course.credits} credit{course.credits !== 1 ? 's' : ''}</span>
                      <button 
                        onClick={() => deleteCourse(course.course_id)}
                        className="text-xs text-aura-text-muted hover:text-aura-destructive transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-8 text-center">
                <BookOpen className="w-12 h-12 text-aura-text-muted mx-auto mb-4" />
                <p className="text-aura-text-muted mb-2">No courses added yet</p>
                <p className="text-xs text-aura-text-subtle">Add your current classes to track your GPA</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Academic Achievements */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-bold text-lg text-white uppercase tracking-wider">
              Academic Achievements
            </h2>
            <Dialog open={showAddAchievement} onOpenChange={setShowAddAchievement}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-white/20 text-white" data-testid="add-achievement-btn">
                  <Award className="w-4 h-4 mr-2" />
                  Add Achievement
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-aura-surface border-white/10 text-white">
                <DialogHeader>
                  <DialogTitle className="font-heading uppercase tracking-tight">Add Achievement</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Achievement Type</Label>
                    <Select value={newAchievement.type} onValueChange={(v) => setNewAchievement({ ...newAchievement, type: v })}>
                      <SelectTrigger className="bg-aura-bg border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-aura-surface border-white/10">
                        {achievementTypes.map(type => (
                          <SelectItem key={type.id} value={type.id} className="text-white">{type.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Title *</Label>
                    <Input
                      value={newAchievement.title}
                      onChange={(e) => setNewAchievement({ ...newAchievement, title: e.target.value })}
                      placeholder="e.g., Honor Roll - Fall Semester"
                      className="bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Date</Label>
                    <Input
                      type="date"
                      value={newAchievement.date}
                      onChange={(e) => setNewAchievement({ ...newAchievement, date: e.target.value })}
                      className="bg-aura-bg border-white/10 text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Description (Optional)</Label>
                    <Textarea
                      value={newAchievement.description}
                      onChange={(e) => setNewAchievement({ ...newAchievement, description: e.target.value })}
                      placeholder="Add details..."
                      className="bg-aura-bg border-white/10 text-white resize-none"
                      rows={2}
                    />
                  </div>
                  <Button onClick={addAchievement} className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-bold">
                    <Award className="w-4 h-4 mr-2" />
                    Add Achievement
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {achievements.length > 0 ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {achievements.map((achievement, idx) => {
                const typeInfo = achievementTypes.find(t => t.id === achievement.type) || achievementTypes[5];
                const Icon = typeInfo.icon;
                return (
                  <Card key={idx} className="bg-gradient-to-r from-yellow-500/10 to-amber-500/10 border-yellow-500/30">
                    <CardContent className="p-4 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-yellow-400" />
                      </div>
                      <div>
                        <p className="font-heading font-bold text-white uppercase tracking-tight">
                          {achievement.title}
                        </p>
                        <p className="text-xs text-aura-text-muted mt-1">
                          {typeInfo.name} • {new Date(achievement.date).toLocaleDateString()}
                        </p>
                        {achievement.description && (
                          <p className="text-sm text-aura-text-muted mt-2">{achievement.description}</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card className="bg-aura-surface border-white/10">
              <CardContent className="p-8 text-center">
                <Award className="w-12 h-12 text-aura-text-muted mx-auto mb-4" />
                <p className="text-aura-text-muted mb-2">No achievements recorded yet</p>
                <p className="text-xs text-aura-text-subtle">Track Honor Roll, awards, and academic milestones</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AcademicsPage;
